import React, { useEffect, useState, useRef } from 'react';
import {
  Box,
  FormControlLabel,
  Checkbox,
  Grid,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Typography,
  Chip,
  Paper,
  Divider,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import MarkdownRenderer from '@/components/shared/MarkdownRenderer';

function TermsOfService({ setValues }) {
  const component_ref = useRef(null);
  const document_types = ['tos', 'privacy'];
  const [documents, setDocuments] = useState({
    tos: { accepted: false, content: '', loading: true },
    privacy: { accepted: false, content: '', loading: true },
  });
  const [current_document, setCurrentDocument] = useState('tos');

  // Load all documents immediately on component mount
  useEffect(() => {
    const loadAllDocuments = async () => {
      try {
        const domain_suffix =
          import.meta.env.VITE_BACKEND_DOMAIN_SUFFIX ||
          import.meta.env.VITE_DOMAIN_SUFFIX ||
          '';
        const fetchPromises = [
          fetch(`https://www.${domain_suffix}/md/tos.md`),
          fetch(`https://www.${domain_suffix}/md/privacy.md`),
        ];
        const responses = await Promise.all(fetchPromises);
        const texts = await Promise.all(
          responses.map((response) => response.text()),
        );
        const updated_documents = {
          tos: { accepted: false, content: texts[0], loading: false },
          privacy: { accepted: false, content: texts[1], loading: false },
        };
        setDocuments(updated_documents);
      } catch (error) {
        console.error('Error loading documents:', error);
        const error_documents = {
          tos: { accepted: false, content: '', loading: false },
          privacy: { accepted: false, content: '', loading: false },
        };
        setDocuments(error_documents);
      }
    };

    loadAllDocuments();
  }, []);

  // Update form values when acceptance status changes
  useEffect(() => {
    if (setValues) {
      const all_accepted = Object.values(documents).every(
        (doc) => doc.accepted,
      );
      setValues({
        terms_accepted: all_accepted,
      });
    }
  }, [documents, setValues]);

  const handleAccept = (doc_type) => {
    setDocuments((prev) => ({
      ...prev,
      [doc_type]: { ...prev[doc_type], accepted: true },
    }));
    // Move to next document if current one is accepted
    const current_index = document_types.indexOf(doc_type);
    if (current_index < document_types.length - 1) {
      // Scroll to top FIRST, then change document
      window.scrollTo({ top: 0, behavior: 'smooth' });
      setCurrentDocument(document_types[current_index + 1]);
    }
  };

  const handleAccordionChange = (event, is_expanded) => {
    // Prevent default accordion behavior that might cause scrolling
    event.preventDefault();
    event.stopPropagation();
  };

  const getDocumentTitle = (doc_type) => {
    const titles = {
      tos: 'Terms of Service',
      privacy: 'Privacy Policy',
    };
    return titles[doc_type];
  };

  const getDocumentIcon = (doc_type) => {
    return <ExpandMoreIcon />;
  };

  return (
    <Box ref={component_ref} width='100%' padding='15px'>
      <Grid container spacing={2}>
        <Grid size={12}>
          <Box>
            <Typography variant='h6' gutterBottom sx={{ mb: 3 }}>
              Terms and Conditions
            </Typography>
            <Typography variant='body2' color='text.secondary' sx={{ mb: 3 }}>
              Please review and accept each agreement to proceed. You must
              accept them in order.
            </Typography>
            {document_types.map((doc_type, index) => {
              const doc = documents[doc_type];
              const is_expanded =
                current_document === doc_type && !doc.accepted;
              const is_disabled =
                index > 0 && !documents[document_types[index - 1]].accepted;

              return (
                <Box key={doc_type} sx={{ mb: 3 }}>
                  <Accordion
                    expanded={is_expanded}
                    disabled={is_disabled}
                    onChange={handleAccordionChange}
                    sx={{
                      mb: 2,
                      '&.Mui-disabled': {
                        backgroundColor: 'grey.100',
                        opacity: 0.6,
                      },
                      '&.Mui-expanded': {
                        margin: '0 0 16px 0',
                      },
                      // Prevent accordion from affecting scroll position
                      '& .MuiAccordionSummary-root': {
                        '&:focus': {
                          outline: 'none',
                        },
                        '&:focus-visible': {
                          outline: 'none',
                        },
                      },
                      // Prevent scroll jumping when accordion expands
                      '& .MuiCollapse-root': {
                        '&.MuiCollapse-entered': {
                          overflow: 'visible',
                        },
                      },
                    }}
                  >
                    <AccordionSummary
                      expandIcon={getDocumentIcon(doc_type)}
                      sx={{
                        '& .MuiAccordionSummary-content': {
                          alignItems: 'center',
                          justifyContent: 'space-between',
                        },
                      }}
                    >
                      <Box
                        sx={{ display: 'flex', alignItems: 'center', gap: 2 }}
                      >
                        <Typography variant='h6'>
                          {getDocumentTitle(doc_type)}
                        </Typography>
                        {is_disabled && (
                          <Chip
                            label='Complete previous document first'
                            color='default'
                            size='small'
                          />
                        )}
                      </Box>
                    </AccordionSummary>
                    <AccordionDetails>
                      <Paper
                        elevation={1}
                        sx={{
                          p: 3,
                          mb: 3,
                          backgroundColor: 'grey.50',
                          border: '1px solid',
                          borderColor: 'grey.200',
                        }}
                      >
                        {doc.loading ? (
                          <Typography>Loading document...</Typography>
                        ) : (
                          <MarkdownRenderer content={doc.content} />
                        )}
                      </Paper>
                    </AccordionDetails>
                  </Accordion>
                  {/* Always render all document contents but hide them */}
                  {!is_expanded && !doc.loading && (
                    <Box sx={{ display: 'none' }}>
                      <MarkdownRenderer content={doc.content} />
                    </Box>
                  )}
                  <FormControlLabel
                    required
                    control={
                      <Checkbox
                        checked={doc.accepted}
                        onChange={() => handleAccept(doc_type)}
                        disabled={doc.loading || is_disabled}
                      />
                    }
                    label={`I accept the ${getDocumentTitle(doc_type)} above.`}
                    sx={{ ml: 0 }}
                  />
                </Box>
              );
            })}
            <Divider sx={{ my: 2 }} />
            <Box
              sx={{
                mt: 2,
                p: 3,
                backgroundColor: 'grey.50',
                borderRadius: 1,
                border: '1px solid',
                borderColor: 'grey.200',
              }}
            >
              <Typography variant='body2' color='text.secondary'>
                <strong>Progress:</strong>{' '}
                {Object.values(documents).filter((doc) => doc.accepted).length}{' '}
                of {Object.keys(documents).length} agreements accepted
              </Typography>
            </Box>
          </Box>
        </Grid>
      </Grid>
    </Box>
  );
}

export default TermsOfService;
